import React, { useState, useEffect, useMemo } from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import { app } from '../firebase/config';
import { CSVLink } from 'react-csv';
import { FaCalendarAlt, FaChartLine, FaUsers, FaRupeeSign, FaFilter, FaDownload, FaWallet, FaHandHoldingUsd } from 'react-icons/fa';

const UserFinancialReport = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewType, setViewType] = useState('all'); // all, monthly, daily, custom
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(true);
  const [showTransactions, setShowTransactions] = useState(false);
  const [allTransactions, setAllTransactions] = useState([]);
  const [availableLevels, setAvailableLevels] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      const db = getDatabase(app);
      const usersRef = ref(db, 'HTAMS/users');
      const levelsRef = ref(db, 'HTAMS/levels');

      try {
        const [usersSnapshot, levelsSnapshot] = await Promise.all([
          get(usersRef),
          get(levelsRef)
        ]);
        
        const users = usersSnapshot.val() || {};
        const levels = levelsSnapshot.val() || {};
        const excludedRoles = new Set(['admin', 'subadmin', 'ca']);
        
        const processedData = [];
        const allTxList = [];
        const uniqueLevels = new Set();

        Object.keys(users).forEach(userId => {
          const user = users[userId];
          const userRole = (user.role || '').toLowerCase();

          if (excludedRoles.has(userRole)) {
            return;
          }

          const analytics = user.analytics || {};
          const transactions = user.transactions || {};
          const commissionHistory = user.commissionHistory || {};

          // Process transactions with dates
          Object.keys(transactions).forEach(txId => {
            const tx = transactions[txId];
            allTxList.push({
              ...tx,
              txId,
              userId,
              userName: user.name || 'N/A',
              userEmail: user.email || 'N/A',
              userRole: user.role || 'N/A',
              date: tx.createdAt || tx.timestamp || tx.at || new Date().toISOString()
            });
          });

          // Also add commission history as transactions
          Object.keys(commissionHistory).forEach(commId => {
            const comm = commissionHistory[commId];
            allTxList.push({
              ...comm,
              txId: commId,
              userId,
              userName: user.name || 'N/A',
              userEmail: user.email || 'N/A',
              userRole: user.role || 'N/A',
              type: 'commission',
              date: comm.at || new Date().toISOString()
            });
          });

          const totalSales = analytics.totalSales || 0;
          // Get commissions from MySales field (string needs to be converted to number)
          const totalCommissions = parseFloat(user.MySales || 0) || 0;
          
          // Only sum withdrawals that are 'approved'
          const totalWithdrawals = Object.values(transactions)
            .filter(tx => {
              const status = (tx.status || '').toLowerCase();
              return tx.type === 'withdrawal' && status === 'approved';
            })
            .reduce((sum, tx) => sum + (tx.amount || 0), 0);

          const balance = totalCommissions - totalWithdrawals;

          // Collect unique levels
          if (user.currentLevel) {
            uniqueLevels.add(user.currentLevel);
          }

          processedData.push({
            id: userId,
            name: user.name || 'N/A',
            email: user.email || 'N/A',
            phone: user.phone || 'N/A',
            role: user.role || 'N/A',
            currentLevel: user.currentLevel || user.level || 'N/A',
            totalSales,
            totalCommissions, // Lifetime total
            totalWithdrawals, // Lifetime total
            balance, // Lifetime balance
            transactionCount: Object.keys(transactions).length,
            transactions: transactions // Store for filtering
          });
        });

        // Get unique levels from users and sort them
        const uniqueLevelsList = Array.from(uniqueLevels).sort();
        
        setReportData(processedData);
        setAllTransactions(allTxList);
        setAvailableLevels(uniqueLevelsList);
      } catch (error) {
        console.error("Error fetching user report data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  // Filter transactions based on date range
  const filteredTransactions = useMemo(() => {
    return allTransactions.filter(tx => {
      const txDate = new Date(tx.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Date filtering
      if (viewType === 'daily') {
        const txDay = new Date(txDate);
        txDay.setHours(0, 0, 0, 0);
        if (txDay.getTime() !== today.getTime()) return false;
      } else if (viewType === 'monthly') {
        if (txDate.getMonth() !== selectedMonth || txDate.getFullYear() !== selectedYear) return false;
      } else if (viewType === 'custom' && customStartDate && customEndDate) {
        const start = new Date(customStartDate);
        const end = new Date(customEndDate);
        end.setHours(23, 59, 59, 999);
        if (txDate < start || txDate > end) return false;
      }

      return true;
    });
  }, [allTransactions, viewType, selectedMonth, selectedYear, customStartDate, customEndDate]);

  // Calculate analytics based on filtered data
  const analytics = useMemo(() => {
    // Apply level filter first
    const levelFilteredUsers = levelFilter === 'all' 
      ? reportData 
      : reportData.filter(u => u.currentLevel === levelFilter);

    // For dashboard cards - use filtered transactions
    const dashboardCommissions = filteredTransactions
      .filter(tx => tx.type === 'commission')
      .reduce((sum, tx) => sum + (tx.amount || 0), 0);

    const dashboardWithdrawals = filteredTransactions
      .filter(tx => tx.type === 'withdrawal' && tx.status?.toLowerCase() === 'approved')
      .reduce((sum, tx) => sum + (tx.amount || 0), 0);

    const totalUsers = levelFilteredUsers.length;
    const totalCommissions = dashboardCommissions;
    const totalWithdrawals = dashboardWithdrawals;
    const totalBalance = totalCommissions - totalWithdrawals;
    const totalSales = levelFilteredUsers.reduce((sum, u) => sum + u.totalSales, 0);

    // Level breakdown - aggregate by currentLevel from users
    const levelBreakdown = {};
    
    levelFilteredUsers.forEach(user => {
      const level = user.currentLevel || 'Unknown';
      if (!levelBreakdown[level]) {
        levelBreakdown[level] = { 
          count: 0, 
          commissions: 0, 
          withdrawals: 0, 
          balance: 0,
          sales: 0 
        };
      }
      
      // Get user's filtered transactions
      const userFilteredTx = filteredTransactions.filter(tx => tx.userId === user.id);
      
      const userCommissions = userFilteredTx
        .filter(tx => tx.type === 'commission')
        .reduce((sum, tx) => sum + (tx.amount || 0), 0);
        
      const userWithdrawals = userFilteredTx
        .filter(tx => tx.type === 'withdrawal' && tx.status?.toLowerCase() === 'approved')
        .reduce((sum, tx) => sum + (tx.amount || 0), 0);
      
      levelBreakdown[level].count++;
      levelBreakdown[level].commissions += userCommissions;
      levelBreakdown[level].withdrawals += userWithdrawals;
      levelBreakdown[level].balance += (userCommissions - userWithdrawals);
      levelBreakdown[level].sales += user.totalSales || 0;
    });

    // Top earners - use lifetime commissions
    const topEarners = [...levelFilteredUsers]
      .sort((a, b) => b.totalCommissions - a.totalCommissions)
      .slice(0, 10);

    return {
      totalUsers,
      totalCommissions,
      totalWithdrawals,
      totalBalance,
      totalSales,
      levelBreakdown,
      topEarners,
      filteredUserData: levelFilteredUsers // Users with lifetime totals
    };
  }, [reportData, filteredTransactions, levelFilter]);

  // CSV data preparation
  const csvData = analytics.filteredUserData.map(user => ({
    userId: user.id,
    name: user.name,
    email: user.email,
    phone: user.phone,
    role: user.role,
    currentLevel: user.currentLevel,
    totalSales: user.totalSales,
    commissions: user.totalCommissions, // Lifetime total
    withdrawals: user.totalWithdrawals, // Lifetime total
    balance: user.balance, // Lifetime balance
    transactions: user.transactionCount
  }));

  const csvHeaders = [
    { label: 'User ID', key: 'userId' },
    { label: 'Name', key: 'name' },
    { label: 'Email', key: 'email' },
    { label: 'Phone', key: 'phone' },
    { label: 'Role', key: 'role' },
    { label: 'Current Level', key: 'currentLevel' },
    { label: 'Total Sales', key: 'totalSales' },
    { label: 'Commissions', key: 'commissions' },
    { label: 'Withdrawals', key: 'withdrawals' },
    { label: 'Balance', key: 'balance' },
    { label: 'Transactions', key: 'transactions' }
  ];

  const getViewTitle = () => {
    switch (viewType) {
      case 'daily':
        return "Today's User Financial Report";
      case 'monthly':
        return `${months[selectedMonth]} ${selectedYear} User Financial Report`;
      case 'custom':
        return customStartDate && customEndDate
          ? `User Financial Report (${new Date(customStartDate).toLocaleDateString()} - ${new Date(customEndDate).toLocaleDateString()})`
          : 'Custom Range User Financial Report';
      default:
        return 'All Time User Financial Report';
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading User Financial Report...</p>
        <style jsx>{`
          .loading-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 400px;
            gap: 20px;
          }
          .spinner {
            width: 50px;
            height: 50px;
            border: 4px solid #f3f4f6;
            border-top-color: #8b5cf6;
            border-radius: 50%;
            animation: spin 0.8s linear infinite;
          }
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          p {
            color: #6b7280;
            font-size: 1.1rem;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="financial-report-container">
      {/* Header */}
      <div className="report-header">
        <div>
          <h1 className="report-title">{getViewTitle()}</h1>
          <p className="report-subtitle">Comprehensive user commission and withdrawal analysis</p>
        </div>
        <div className="header-actions">
          <button className="filter-toggle-btn" onClick={() => setShowFilters(!showFilters)}>
            <FaFilter /> {showFilters ? 'Hide' : 'Show'} Filters
          </button>
          <CSVLink 
            data={csvData} 
            headers={csvHeaders} 
            filename={`user-financial-report-${viewType}-${new Date().toISOString().split('T')[0]}.csv`}
            className="export-btn"
          >
            <FaDownload /> Export CSV
          </CSVLink>
        </div>
      </div>

      {/* Filters Section */}
      {showFilters && (
        <div className="filters-section">
          <div className="filter-group">
            <label>View Type</label>
            <div className="view-type-buttons">
              <button className={`view-btn ${viewType === 'all' ? 'active' : ''}`} onClick={() => setViewType('all')}>All Time</button>
              <button className={`view-btn ${viewType === 'daily' ? 'active' : ''}`} onClick={() => setViewType('daily')}>Today</button>
              <button className={`view-btn ${viewType === 'monthly' ? 'active' : ''}`} onClick={() => setViewType('monthly')}>Monthly</button>
              <button className={`view-btn ${viewType === 'custom' ? 'active' : ''}`} onClick={() => setViewType('custom')}>Custom Range</button>
            </div>
          </div>

          {viewType === 'monthly' && (
            <div className="filter-row">
              <div className="filter-group">
                <label>Month</label>
                <select value={selectedMonth} onChange={(e) => setSelectedMonth(parseInt(e.target.value))}>
                  {months.map((month, index) => (
                    <option key={index} value={index}>{month}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>Year</label>
                <select value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))}>
                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {viewType === 'custom' && (
            <div className="filter-row">
              <div className="filter-group">
                <label>Start Date</label>
                <input type="date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} />
              </div>
              <div className="filter-group">
                <label>End Date</label>
                <input type="date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} />
              </div>
            </div>
          )}

          <div className="filter-group">
            <label>Filter by Level</label>
            <select value={levelFilter} onChange={(e) => setLevelFilter(e.target.value)}>
              <option value="all">All Levels</option>
              {availableLevels.map(level => (
                <option key={level} value={level}>{level}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Analytics Cards */}
      <div className="analytics-section-wrapper">
        <div className="analytics-info">
          <p className="period-note">
            {viewType === 'all' ? '📊 Showing all-time analytics' : 
             viewType === 'daily' ? '📊 Today\'s analytics' :
             viewType === 'monthly' ? `📊 Analytics for ${months[selectedMonth]} ${selectedYear}` :
             '📊 Custom period analytics'}
          </p>
        </div>
        <div className="analytics-cards">
          <div className="analytics-card users">
            <div className="card-content">
              <h3>Total Users</h3>
              <p className="card-value">{analytics.totalUsers}</p>
            </div>
          </div>
          <div className="analytics-card commissions">
            <div className="card-content">
              <h3>Commissions</h3>
              <p className="card-value">₹{analytics.totalCommissions.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
            </div>
          </div>
          <div className="analytics-card withdrawals">
            <div className="card-content">
              <h3>Withdrawals</h3>
              <p className="card-value">₹{analytics.totalWithdrawals.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
            </div>
          </div>
          <div className="analytics-card balance">
            <div className="card-content">
              <h3>Net Balance</h3>
              <p className="card-value">₹{analytics.totalBalance.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Analysis Grid */}
      <div className="analysis-grid">
        {/* Level Breakdown */}
        <div className="analysis-section">
          <h3>🎯 Level-wise Breakdown</h3>
          {Object.keys(analytics.levelBreakdown).length > 0 ? (
            <div className="breakdown-table">
              {Object.entries(analytics.levelBreakdown)
                .sort((a, b) => b[1].commissions - a[1].commissions)
                .map(([level, data]) => (
                  <div key={level} className="breakdown-row">
                    <div className="level-info">
                      <span className="level-name">{level}</span>
                      <span className="level-count">{data.count} users</span>
                    </div>
                    <div className="level-amounts">
                      <div className="amount-row">
                        <span className="amount-label">Sales:</span>
                        <span className="amount-value">₹{data.sales.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                      </div>
                      <div className="amount-row">
                        <span className="amount-label">Comm:</span>
                        <span className="stat-amount">₹{data.commissions.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <div className="no-breakdown-data">
              <p>No level data available for selected period</p>
            </div>
          )}
        </div>

        {/* Top Earners */}
        <div className="analysis-section">
          <h3>🏆 Top Earners (Lifetime)</h3>
          {analytics.topEarners && analytics.topEarners.length > 0 ? (
            <div className="top-earners-scroll">
              <div className="breakdown-table">
                {analytics.topEarners.slice(0, 10).map((user, index) => (
                  <div key={user.id} className="breakdown-row earner-row">
                    <div className="earner-info">
                      <span className="rank">#{index + 1}</span>
                      <div className="earner-details">
                        <span className="earner-name">{user.name || 'N/A'}</span>
                        <span className="earner-level">{user.currentLevel || 'N/A'}</span>
                      </div>
                    </div>
                    <span className="stat-amount">₹{(user.totalCommissions || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="no-breakdown-data">
              <p>No earner data available</p>
            </div>
          )}
        </div>
      </div>

      {/* Detailed Users Table */}
      <div className="table-section">
        <div className="table-header">
          <h3>💼 Detailed User Financial Data</h3>
          <p className="table-subtitle">Showing lifetime totals for each user</p>
        </div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>Level</th>
                <th>Commissions</th>
                <th>Withdrawals</th>
                <th>Balance</th>
                <th>Transactions</th>
              </tr>
            </thead>
            <tbody>
              {analytics.filteredUserData.length > 0 ? (
                analytics.filteredUserData.map((user) => (
                  <tr key={user.id}>
                    <td className="user-cell">
                      <div className="user-name">{user.name}</div>
                      <div className="user-email">{user.email}</div>
                      {user.phone !== 'N/A' && <div className="user-phone">{user.phone}</div>}
                    </td>
                    <td>
                      <span className="level-badge">{user.currentLevel}</span>
                    </td>
                    <td className="amount commission-amount">₹{user.totalCommissions.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                    <td className="amount withdrawal-amount">₹{user.totalWithdrawals.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                    <td className={`amount ${user.balance >= 0 ? 'positive-balance' : 'negative-balance'}`}>
                      ₹{user.balance.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </td>
                    <td className="transaction-count">{user.transactionCount}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="no-data">
                    <div className="no-data-message">
                      <FaChartLine size={48} />
                      <p>No user data found for the selected filters.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <style jsx>{`
        /* Container - Extra Compact */
        .financial-report-container {
          padding: 10px;
          background: #f9fafb;
          min-height: 100vh;
        }

        /* Header - Extra Compact */
        .report-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 8px;
          background: white;
          padding: 8px 12px;
          border-radius: 6px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
        }
        .report-title {
          font-size: 1.1rem;
          font-weight: 700;
          color: #1f2937;
          margin: 0;
          line-height: 1.2;
        }
        .report-subtitle {
          color: #6b7280;
          margin: 0;
          font-size: 0.7rem;
        }
        .header-actions {
          display: flex;
          gap: 8px;
        }
        .filter-toggle-btn, .export-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border: none;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 0.8rem;
        }
        .filter-toggle-btn {
          background: #8b5cf6;
          color: white;
        }
        .filter-toggle-btn:hover {
          background: #7c3aed;
        }
        .export-btn {
          background: #10b981;
          color: white;
          text-decoration: none;
        }
        .export-btn:hover {
          background: #059669;
        }

        /* Filters Section - Extra Compact */
        .filters-section {
          background: white;
          padding: 8px;
          border-radius: 6px;
          margin-bottom: 8px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
        }
        .filter-group {
          margin-bottom: 8px;
        }
        .filter-group:last-child {
          margin-bottom: 0;
        }
        .filter-group label {
          display: block;
          font-weight: 600;
          color: #374151;
          margin-bottom: 4px;
          font-size: 0.75rem;
        }
        .filter-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
        }
        .view-type-buttons {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .view-btn {
          padding: 6px 10px;
          border: 1px solid #e5e7eb;
          background: white;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 600;
          font-size: 0.75rem;
          transition: all 0.2s;
        }
        .view-btn:hover {
          border-color: #8b5cf6;
          color: #8b5cf6;
        }
        .view-btn.active {
          background: #8b5cf6;
          color: white;
          border-color: #8b5cf6;
        }
        .filter-group select, .filter-group input[type="date"] {
          width: 100%;
          padding: 6px 8px;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          font-size: 0.85rem;
          transition: all 0.2s;
        }
        .filter-group select:focus, .filter-group input[type="date"]:focus {
          outline: none;
          border-color: #8b5cf6;
          box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.1);
        }

        /* Analytics Section - Extra Compact */
        .analytics-section-wrapper {
          margin-bottom: 8px;
        }
        .analytics-info {
          margin-bottom: 6px;
        }
        .period-note {
          margin: 0;
          padding: 6px 10px;
          background: #f0f9ff;
          border-left: 2px solid #3b82f6;
          border-radius: 4px;
          font-size: 0.7rem;
          color: #1e40af;
          font-weight: 500;
        }

        /* Analytics Cards - Extra Compact */
        .analytics-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
          gap: 8px;
        }
        .analytics-card {
          background: white;
          padding: 8px 10px;
          border-radius: 6px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
          display: flex;
          align-items: center;
          gap: 8px;
          transition: transform 0.2s, box-shadow 0.2s;
        }
        .analytics-card:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
        }
        .card-icon {
          width: 36px;
          height: 36px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1rem;
          color: white;
          flex-shrink: 0;
        }
        .analytics-card.users .card-icon {
          background: linear-gradient(135deg, #3b82f6, #2563eb);
        }
        .analytics-card.commissions .card-icon {
          background: linear-gradient(135deg, #10b981, #059669);
        }
        .analytics-card.withdrawals .card-icon {
          background: linear-gradient(135deg, #f59e0b, #d97706);
        }
        .analytics-card.balance .card-icon {
          background: linear-gradient(135deg, #8b5cf6, #7c3aed);
        }
        .card-content {
          flex: 1;
          min-width: 0;
        }
        .card-content h3 {
          margin: 0 0 2px 0;
          font-size: 0.65rem;
          color: #6b7280;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }
        .card-value {
          margin: 0;
          font-size: 1.2rem;
          font-weight: 700;
          color: #1f2937;
          line-height: 1.1;
        }

        /* Analysis Grid - Extra Compact */
        .analysis-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 8px;
          margin-bottom: 8px;
        }
        .analysis-section {
          background: white;
          padding: 8px;
          border-radius: 6px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
        }
        .analysis-section h3 {
          margin: 0 0 6px 0;
          font-size: 0.85rem;
          color: #1f2937;
          font-weight: 600;
        }
        .breakdown-table {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .breakdown-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 6px 8px;
          background: #f9fafb;
          border-radius: 4px;
          gap: 6px;
        }
        .level-name, .role-name {
          font-weight: 600;
          color: #1f2937;
          font-size: 0.8rem;
          display: block;
        }
        .level-info, .role-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .level-count, .role-count {
          font-size: 0.65rem;
          color: #6b7280;
        }
        .level-amounts, .role-amounts {
          display: flex;
          flex-direction: column;
          gap: 2px;
          text-align: right;
        }
        .amount-row {
          display: flex;
          justify-content: flex-end;
          gap: 4px;
          align-items: center;
        }
        .amount-label {
          font-size: 0.65rem;
          color: #9ca3af;
        }
        .amount-value {
          font-size: 0.7rem;
          font-weight: 600;
          color: #3b82f6;
        }
        .breakdown-stats {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .stat-item {
          font-size: 0.7rem;
          color: #6b7280;
        }
        .stat-amount {
          font-weight: 600;
          color: #059669;
          font-size: 0.75rem;
        }
        .no-breakdown-data {
          text-align: center;
          padding: 20px;
          color: #9ca3af;
          font-size: 0.75rem;
        }
        .no-breakdown-data p {
          margin: 0;
        }
        /* Top Earners Scrollable */
        .top-earners-scroll {
          max-height: 300px;
          overflow-y: auto;
          overflow-x: hidden;
        }
        .top-earners-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .top-earners-scroll::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }
        .top-earners-scroll::-webkit-scrollbar-thumb {
          background: #8b5cf6;
          border-radius: 3px;
        }
        .top-earners-scroll::-webkit-scrollbar-thumb:hover {
          background: #7c3aed;
        }
        .earner-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .earner-info {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .earner-details {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .rank {
          font-weight: 700;
          color: #8b5cf6;
          font-size: 0.8rem;
          min-width: 25px;
        }
        .earner-name {
          font-weight: 600;
          color: #1f2937;
          font-size: 0.75rem;
        }
        .earner-level {
          font-size: 0.65rem;
          color: #7c3aed;
          background: #f3e8ff;
          padding: 2px 5px;
          border-radius: 3px;
          font-weight: 600;
        }

        /* Tables - Extra Compact */
        .table-section {
          background: white;
          padding: 8px;
          border-radius: 6px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
        }
        .table-header {
          margin-bottom: 6px;
        }
        .table-section h3 {
          margin: 0 0 2px 0;
          font-size: 0.85rem;
          color: #1f2937;
          font-weight: 600;
        }
        .table-subtitle {
          margin: 0;
          font-size: 0.65rem;
          color: #6b7280;
          font-style: italic;
        }
        .table-container {
          overflow-x: auto;
          border-radius: 4px;
          border: 1px solid #e5e7eb;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.75rem;
        }
        th, td {
          padding: 6px 8px;
          text-align: left;
          border-bottom: 1px solid #e5e7eb;
        }
        th {
          background-color: #f9fafb;
          font-weight: 600;
          text-transform: uppercase;
          color: #374151;
          font-size: 0.65rem;
          letter-spacing: 0.2px;
        }
        tbody tr {
          transition: background-color 0.2s;
        }
        tbody tr:hover {
          background-color: #f9fafb;
        }
        tbody tr:last-child td {
          border-bottom: none;
        }
        .user-cell {
          min-width: 160px;
        }
        .user-name {
          font-weight: 500;
          color: #1f2937;
          font-size: 0.8rem;
        }
        .user-email {
          font-size: 0.65rem;
          color: #6b7280;
          margin-top: 1px;
        }
        .user-phone {
          font-size: 0.65rem;
          color: #9ca3af;
          margin-top: 1px;
        }
        .role-badge, .level-badge {
          display: inline-block;
          padding: 3px 8px;
          background: #e0e7ff;
          color: #4f46e5;
          border-radius: 8px;
          font-size: 0.65rem;
          font-weight: 600;
          text-transform: capitalize;
        }
        .level-badge {
          background: linear-gradient(135deg, #f3e8ff, #e9d5ff);
          color: #7c3aed;
          font-weight: 700;
        }
        .amount {
          font-weight: 700;
          font-size: 0.8rem;
        }
        .commission-amount {
          color: #059669;
        }
        .withdrawal-amount {
          color: #d97706;
        }
        .positive-balance {
          color: #059669;
        }
        .negative-balance {
          color: #dc2626;
        }
        .transaction-count {
          text-align: center;
          color: #8b5cf6;
          font-weight: 600;
          font-size: 0.75rem;
        }
        .no-data {
          text-align: center;
          padding: 40px 20px;
        }
        .no-data-message {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
          color: #9ca3af;
        }
        .no-data-message p {
          margin: 0;
          font-size: 1.1rem;
        }

        /* Responsive - Compact Mobile */
        @media (max-width: 768px) {
          .financial-report-container {
            padding: 12px;
          }
          .report-header {
            flex-direction: column;
            gap: 12px;
            padding: 12px 16px;
          }
          .header-actions {
            width: 100%;
            flex-direction: column;
            gap: 8px;
          }
          .filter-toggle-btn, .export-btn {
            width: 100%;
            justify-content: center;
            padding: 10px 16px;
            font-size: 0.9rem;
          }
          .report-title {
            font-size: 1.25rem;
          }
          .analytics-cards {
            grid-template-columns: 1fr;
            gap: 8px;
          }
          .analytics-card {
            padding: 12px 14px;
          }
          .card-icon {
            width: 40px;
            height: 40px;
          }
          .card-value {
            font-size: 1.25rem;
          }
          .analysis-grid {
            grid-template-columns: 1fr;
            gap: 8px;
          }
          .view-type-buttons {
            flex-direction: column;
          }
          .view-btn {
            width: 100%;
          }
          .table-section {
            padding: 12px;
          }
        }
      `}</style>
    </div>
  );
};

export default UserFinancialReport;
